import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CircularProgress, Container, Grid, Typography } from "@mui/material"
import { indigo } from "@mui/material/colors"
import { makeStyles } from "tss-react/mui"
import { Player } from "@lottiefiles/react-lottie-player"
import { LOGIN_ROUTE } from "../../../constants/routes"
import { Logger } from "../../../lib/logging"
import { UnreachableCaseError } from "../../../lib/lang"
import { createEnumChecker } from "../../../lib/enums"
import { StandardButton } from "../../../shared/components/buttons/StandardButton"
import { LanguageSelector } from "../../../shared/components/languageSelector/LanguageSelector"
import { ApiError, AppError } from "../../../core/api/errorTypes"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { AccountChangeType } from "../loginTypes"
import { confirmAccountChange } from "../loginApi"
import FrontpageLogo from "../assets/gopay-logo-horizontal-white.svg"
import SuccessAnim from "../assets/success-lottie-anim.json"
import FailureAnim from "../assets/failure-lottie-anim.json"

enum FormStatus {
    INITIAL = "INITIAL",
    CONFIRMING = "CONFIRMING",
    SUCCESS = "SUCCESS",
    FAILURE = "FAILURE",
}

enum FlowType {
    AUTO = "AUTO",
    MANUAL = "MANUAL",
}

type InitialState = Readonly<{
    status: FormStatus.INITIAL
    flowType: FlowType
}>
type ConfirmingState = Readonly<{
    status: FormStatus.CONFIRMING
    flowType: FlowType
}>
type SuccessState = Readonly<{
    status: FormStatus.SUCCESS
    flowType: FlowType
    displayMessage: string
}>
type FailureState = Readonly<{
    status: FormStatus.FAILURE
    flowType: FlowType
    displayMessage: string | undefined
}>
type FormState =
    | InitialState
    | ConfirmingState
    | SuccessState
    | FailureState

function getFlowType(changeType: AccountChangeType) {
    switch (changeType) {
        case AccountChangeType.EMAIL:
            return FlowType.AUTO
        default:
            throw new UnreachableCaseError(changeType)
    }
}

const isAccountChangeType = createEnumChecker(AccountChangeType)

const useStyles = makeStyles()((theme) => ({
    page: {
        height: "100vh",
        color: theme.palette.common.white,
        backgroundImage: `linear-gradient(${indigo[300]}, ${indigo[600]})`,
        padding: "var(--space-s-m) var(--space-xl-2xl)",

        [theme.breakpoints.down("sm")]: {
            padding: "0.5rem",
            paddingBottom: 0,
        }
    },
    topBar: {
    },
    frontPageLogo: {
        width: "8.625rem",
        height: "auto",

        [theme.breakpoints.down("md")]: {
            width: "6rem"
        },
        [theme.breakpoints.down("sm")]: {
            //visibility: "hidden",
        },
    },
    contentGrid: {
        height: "100%",
        justifyContent: "center",

        [theme.breakpoints.down("sm")]: {
            justifyContent: "flex-end",
        },
    },
    container: {
        padding: "var(--space-l-xl) var(--space-s-m)",
        color: theme.palette.common.black,
        backgroundColor: theme.palette.common.white,

        [theme.breakpoints.up("sm")]: {
            padding: "var(--space-2xl-3xl) var(--space-m-l)",
        },
    },
    outerGrid: {
        height: "100%",
        rowGap: "var(--space-m-l)",
    },
    pageTitle: {
        fontSize: "var(--font-size-step-3)",
        paddingBottom: "var(--space-xs)",
    },
    pageSubtitle: {
        lineHeight: 1.5,
        //paddingBottom: "0.5rem",
    },
    formHeader: {
        fontSize: "var(--font-size-step-1)",
        paddingBottom: "var(--space-s)",
    },
    form: {
        rowGap: "var(--space-xs)",
    },
    submitButton: {
        minWidth: "15rem",
//        alignSelf: "flex-end",

        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    statusLabel: {
        fontSize: "var(--font-size-step-2)",
    },
    statusSublabel: {
        fontSize: "var(--font-size-step-1)",
    },
    spinner: {
        margin: "2rem",
    },
    animation: {
        height: "10rem",
    },
}))

function useConfirmAccountChangeParams() {
    const [searchParams] = useSearchParams()

    const changeTypeRaw = searchParams.get("changeType")
    const changeType = isAccountChangeType(changeTypeRaw) ? changeTypeRaw : undefined

    const oneTimePassword = searchParams.get("otp")

    let errorMessage = undefined

    if (!changeType) {
        errorMessage = `Confirm account change link was missing valid information: changeType [raw value received: '${changeTypeRaw ? changeTypeRaw : ""}']`
        throw new AppError("E_INVALID_LINK", "Invalid confirm account change link", errorMessage)
    } else if (!oneTimePassword) {
        errorMessage = `Confirm account change link was missing information: otp`
        throw new AppError("E_INVALID_LINK", "Invalid confirm account change link", errorMessage)
    }

    return {
        changeType,
        oneTimePassword,
    }
}

type EmailContentProps = Readonly<{
    formState: FormState
}>

function EmailContent({ formState }: EmailContentProps) {
    const { classes } = useStyles()
    const navigate = useNavigate()

    return (
        <>
            {(formState.status === FormStatus.INITIAL || formState.status === FormStatus.CONFIRMING) && (
                <>
                    <CircularProgress size="10rem" className={classes.spinner} />
                    <LocalizedStrict id="confirm-account-change-confirming-email-message">
                        <Typography variant="h5" align="center" className={classes.statusLabel}>Confirming email address...</Typography>
                    </LocalizedStrict>
                </>
            )}
            {formState.status === FormStatus.SUCCESS && (
                <>
                    <Player src={SuccessAnim} autoplay keepLastFrame className={classes.animation} />
                    <Typography variant="h5" align="center" className={classes.statusLabel}>{formState.displayMessage}</Typography>
                    <LocalizedStrict id="confirm-account-change-login-button">
                        <StandardButton
                            onClick={() => navigate(LOGIN_ROUTE)}
                            fullWidth={false}
                            className={classes.submitButton}
                        >
                            Sign in
                        </StandardButton>
                    </LocalizedStrict>
                </>
            )}
            {formState.status === FormStatus.FAILURE && (
                <>
                    <Player src={FailureAnim} autoplay keepLastFrame className={classes.animation} />
                    {formState.displayMessage ? (
                        <Typography variant="h5" align="center" className={classes.statusLabel}>{formState.displayMessage}</Typography>
                    ) : (
                        <LocalizedStrict id="confirm-account-change-failure-message">
                            <Typography variant="h5" align="center" className={classes.statusLabel}>Failed to confirm change</Typography>
                        </LocalizedStrict>
                    )}
                </>
            )}
        </>
    )
}

type Props = Readonly<{}>

export function ConfirmAccountChange({}: Props) {
    const { classes } = useStyles()

    const {
        changeType,
        oneTimePassword,
    } = useConfirmAccountChangeParams()

    const [formState, setFormState] = useState<FormState>({ status: FormStatus.INITIAL, flowType: getFlowType(changeType) })

    const { mutateAsync: callConfirmAccountChange } = usePublicApiMutation(confirmAccountChange, {
        async onSuccess(_params, data, _context) {
            setFormState((prev) => ({
                status: FormStatus.SUCCESS,
                flowType: prev.flowType,
                displayMessage: data.serviceResponse.displayMessage,
            }))
        },
        async onError(_params, error, _context) {
            const displayMessage = error instanceof ApiError && error.hasApiMessage ? error.displayMessage : undefined

            setFormState((prev) => ({
                status: FormStatus.FAILURE,
                flowType: prev.flowType,
                displayMessage,
            }))
        },
    })

    async function handleConfirmAccountChange() {
        const logger = new Logger("confirm-account-change")

        setFormState((prev) => ({
            status: FormStatus.CONFIRMING,
            flowType: prev.flowType,
        }))

        try {
            await callConfirmAccountChange({
                changeType,
                authentication: {
                    type: "ONE_TIME_TOKEN",
                    token: oneTimePassword,
                }
            })
        } catch (err: unknown) {
            logger.error("Failed to confirm account change", err)
        }
    }

    useEffect(() => {
        const { status, flowType } = formState
        if (status === FormStatus.INITIAL && flowType === FlowType.AUTO) {
            handleConfirmAccountChange()
                .catch((_err: unknown) => { /* ignore */ })
        }
    }, [formState])

    return (
        <Grid container direction="column" wrap="nowrap" component="main" className={classes.page}>
            <Grid item container wrap="nowrap" justifyContent="space-between" className={classes.topBar} >
                <img src={FrontpageLogo} className={classes.frontPageLogo} />
                <LanguageSelector color="inherit" />
            </Grid>
            <Grid item container direction="column" wrap="nowrap" className={classes.contentGrid}>
                <Container maxWidth="md" component="section" className={classes.container}>
                    <Grid container direction="column" wrap="nowrap" justifyContent="center" alignItems="center" className={classes.outerGrid}>
                        {changeType === AccountChangeType.EMAIL && (
                            <EmailContent formState={formState} />
                        )}
                    </Grid>
                </Container>
            </Grid>
        </Grid>
    )
}
